import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, of, Subject, throwError } from 'rxjs';
import { catchError, filter, flatMap, last, map, tap } from 'rxjs/operators';
import { SohoModalDialogService } from 'ids-enterprise-ng';

import { LMModelMultiPartFields } from '@form/model/lm-model';
import { LMFileUploadProgressComponent } from './lm-file-upload-progress.component';
import { LMBusyIndicatorService } from '../lm-busyindicator.service';

@Injectable()
export class LMFileUploadService {
  private EXTRA_JSON_DATA = 'extraJSONData';

  constructor(private http: HttpClient, private sohoModalDialogService: SohoModalDialogService, private lmBusyIndicatorService: LMBusyIndicatorService) {}

  public rotateOrFlip(ctx: CanvasRenderingContext2D, orientation: number, orientationOption: string, width: number, height: number) {
    // rotate or flip according to orientation
    switch (orientation) {
      case 0:
        if (orientationOption && orientationOption === 'portrait') {
          this.translatePortrait(ctx, width, height);
        } else if (orientationOption && orientationOption === 'landscape') {
          ctx.translate(height / 3.53, width / 3.53);
        }
        break;
      case 2:
        this.translatePortrait(ctx, width, height);
        this.flip(ctx);
        break;
      case 4:
        this.translatePortrait(ctx, width, height);
        this.flip(ctx);
        this.rotate180(ctx);
        break;
      case 3:
        this.translatePortrait(ctx, width, height);
        this.rotate180(ctx);
        break;
      case 5:
        this.translateLandscape(ctx, width, height);
        this.flip(ctx);
        this.rotate90(ctx, 'clockwise');
        break;
      case 6:
        if (orientationOption && orientationOption === 'portrait') {
          this.translatePortrait(ctx, width, height);
          this.rotate90(ctx, 'clockwise');
          ctx.scale(height / width, width / height);
        } else {
          this.translateLandscape(ctx, width, height);
          this.rotate90(ctx, 'clockwise');
        }
        break;
      case 7:
        this.translateLandscape(ctx, width, height);
        this.flip(ctx);
        this.rotate90(ctx, 'counterclockwise');
        break;
      case 8:
        this.translateLandscape(ctx, width, height);
        this.rotate90(ctx, 'counterclockwise');
        break;
      default: break;
    }
  }

  private flip(context) {
    context.scale(-1, 1);
  };

  private rotate90(context, where) {
    const degrees = where === 'clockwise' ? 90 : -90;
    context.rotate(degrees * Math.PI / 180);
  };

  private rotate180(context) {
    context.rotate(Math.PI);
  };

  private translatePortrait(context, width, height) {
    context.translate(width / 2, height / 2);
  };

  private translateLandscape(context, width, height) {
    context.translate(height / 2, width / 2);
  };

  // Utility function to convert a canvas to a BLOB
  public dataURLToBlob(dataURL: string): Blob {
    const BASE64_MARKER = ';base64,';
    let parts: string[] = [];
    let contentType: string = null;
    let raw: string = null;

    if (dataURL.indexOf(BASE64_MARKER) === -1) {
      parts = dataURL.split(',');
      contentType = parts[0].split(':')[1];
      raw = parts[1];
      return new Blob([raw], {type: contentType});
    }
    parts = dataURL.split(BASE64_MARKER);
    contentType = parts[0].split(':')[1];
    raw = window.atob(parts[1]);
    const rawLength: number = raw.length;
    const uInt8Array: Uint8Array = new Uint8Array(rawLength);
    for (let i = 0; i < rawLength; ++i) {
      uInt8Array[i] = raw.charCodeAt(i);
    }
    return new Blob([uInt8Array], {type: contentType});
  };

  public makeTemplateFileRequest(panelId: string, formName: string, url: string, filesList: Array<LMModelMultiPartFields>, data: any): Observable<any> {
    const templateFormName = panelId + '_' + formName + '_';
    const formData: FormData = new FormData();

    filesList.forEach((value: LMModelMultiPartFields, index: number) => {
      this.buildFormData(templateFormName + index, formData, value);
    });

    return this.executeRequest(url, formData, data);
  }

  public makeFileRequest(formName: string, url: string, files: LMModelMultiPartFields, data: any): Observable<any> {
    const formData: FormData = new FormData();
    this.buildFormData(formName, formData, files);
    return this.executeRequest(url, formData, data);
  }

  private executeRequest(url: string, formData: FormData, data: any): Observable<LMActionResult> {
    const dialogAfterOpen = new Subject();
    const dialog = this.sohoModalDialogService.modal(LMFileUploadProgressComponent);

    dialog.title(Soho.Locale.translate('FileUploadProgress'));
    dialog.noRefocus(true);
    dialog.afterOpen(() => {
      dialogAfterOpen.next();
      dialogAfterOpen.complete();
    });

    formData.append(this.EXTRA_JSON_DATA, JSON.stringify(data));
    const headers = new HttpHeaders();

    dialog.open();
    return dialogAfterOpen.pipe(
      // start file upload along with entire form data
      flatMap(() => this.http.post(url, formData, { headers, reportProgress: true, observe: 'events' })),
      filter((event: HttpEvent<LMActionResult>) => [ HttpEventType.UploadProgress, HttpEventType.Response ].includes(event.type)),
      // compute and set % of file uploaded:
      tap((event: HttpEvent<LMActionResult>) => {
        if (event.type === HttpEventType.UploadProgress) {
          const percent = Math.round(100 * event.loaded / event.total);
          if (dialog.componentDialog) {
            dialog.componentDialog.progress = percent;
          }
          // close modal as soon as 100% uploaded
          if (percent >= 100 ) {
            dialog.close();
            this.lmBusyIndicatorService.showBusyIndicator();
          }
        }
      }),
      // last emitted value is the http response
      last((event: HttpEvent<LMActionResult>) => event.type === HttpEventType.Response),
      map((event: HttpResponse<LMActionResult>) => event.body),
      // close modal then propagate error
      catchError(error => {
        dialog.close();
        this.lmBusyIndicatorService.showBusyIndicator();
        return throwError(error);
      })
    );
  }

  private buildFormData(formName: string, formData: FormData, files: LMModelMultiPartFields) {
    const keys: Array<string> = Object.keys(files);

    keys.forEach((fieldName) => {
      const file: File | Blob = files[ fieldName ];
      formData.append(formName + '_' + fieldName, file);
    });
  }
}
